import { useCallback } from "react"

import {
  PlaidErrorCodeEnum,
  type PlaidLinkTokenError,
} from "@boundless/bl-api-client/customer/models"
import { PlaidErrorCodeEnumLabels } from "@boundless/bl-api-client/customer/models/PlaidErrorCodeEnumLabels"
import {
  getCustomerPlaidLinkRetrieveQueryKey,
  useCustomerCompanyPartialUpdate,
  useCustomerCompanyRetrieveSuspense,
  useCustomerPlaidExchangeCreate,
  useCustomerPlaidLinkRetrieve,
} from "@boundless/bl-api-client/customer/services"
import { useQueryClient } from "@tanstack/react-query"
import { getRouteApi } from "@tanstack/react-router"
import { LinkIcon } from "lucide-react"
import { usePlaidLink } from "react-plaid-link"
import { toast } from "sonner"

import { PendingComponent } from "@/components/error/PendingComponent"
import { FormHeader } from "@/components/form-elements/FormHeader"
import { MainCard } from "@/components/layout/SectionCards"
import { Button } from "@/components/ui/button"
import type { ErrorType } from "@/lib/clients"
import { CompanyFlowContext } from "@/lib/flows/company"
import PlaidLogo from "@/svg/vendors/plaid.svg?react"

const expectedErrors = Object.values(PlaidErrorCodeEnum)
const nextErrors: PlaidErrorCodeEnum[] = [
  "already_linked",
  "country_not_supported",
]

export function isUnexpectedError(
  error: ErrorType<PlaidLinkTokenError>,
): boolean {
  const errorCode = error.response?.data.code
  return !!errorCode && !expectedErrors.includes(errorCode)
}

export function PlaidConnectCard() {
  const routeApi = getRouteApi("/_dashboard/company/plaid")
  const queryClient = useQueryClient()
  const { companyUuid } = routeApi.useRouteContext()

  const flowActorRef = CompanyFlowContext.useActorRef()

  const goNext = () => flowActorRef.send({ type: "next" })
  const goBack = () => {
    flowActorRef.send({
      type: "setHasMultipleOwners",
      hasMultipleOwners: company.hasMultipleOwners,
    })
    flowActorRef.send({ type: "back" })
  }

  const {
    data: token,
    isPending,
    isError,
    error,
  } = useCustomerPlaidLinkRetrieve(companyUuid, {
    query: {
      meta: {
        // Suppress the generic toast for expected Plaid errors so we can show inline messaging instead.
        shouldDisplayErrorToast: (error) =>
          isUnexpectedError(error as ErrorType<PlaidLinkTokenError>),
      },
    },
  })

  const { data: company, queryKey: companyQueryKey } =
    useCustomerCompanyRetrieveSuspense(companyUuid)

  const { mutate: updateCompany } = useCustomerCompanyPartialUpdate({
    mutation: {
      onSuccess: (updatedCompanyData) => {
        queryClient.setQueryData(companyQueryKey, updatedCompanyData)
        goNext()
      },
    },
  })

  const handleSkip = useCallback(() => {
    if (company.skippedHeronLink) {
      goNext()
    } else {
      // Update the company to indicate that link was skipped, and
      // trigger goNext (as part of the onSuccess callback)
      updateCompany({
        uuid: companyUuid,
        data: { skippedHeronLink: true },
      })
    }
  }, [companyUuid, updateCompany, company.skippedHeronLink])

  const errorCode = error?.response?.data.code || null
  const showNextButton = !!errorCode && nextErrors.includes(errorCode)

  if (isPending) {
    return <PendingComponent />
  }

  if (isError && isUnexpectedError(error)) {
    throw error
  }

  return (
    <MainCard>
      <FormHeader title="Connect Your Bank Account" />

      <div className="mt-8 flex flex-col items-center justify-center gap-8 sm:mt-12 lg:mt-20 lg:gap-12">
        <PlaidLogo className="w-64 max-w-full" />
        <div className="flex w-full max-w-sm flex-col gap-4">
          {token && (
            <PlaidModalButton companyUuid={companyUuid} token={token} />
          )}
          <Button variant="link" onClick={handleSkip}>
            Skip for now
          </Button>
          {errorCode && (
            <p className="text-center text-base sm:text-lg">
              {PlaidErrorCodeEnumLabels[errorCode]}
            </p>
          )}
        </div>
      </div>

      <div className="mt-10 flex flex-col gap-2 sm:mt-12 sm:flex-row sm:justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={goBack}
          className="w-full sm:w-auto"
        >
          Back
        </Button>

        {showNextButton && (
          <Button
            type="button"
            onClick={handleSkip}
            className="w-full sm:w-auto"
          >
            Next
          </Button>
        )}
      </div>
    </MainCard>
  )
}

function PlaidModalButton({
  companyUuid,
  token,
}: {
  companyUuid: string
  token: string
}) {
  const flowActorRef = CompanyFlowContext.useActorRef()
  const queryClient = useQueryClient()

  const goNext = () => flowActorRef.send({ type: "next" })

  const { mutate: exchangeToken } = useCustomerPlaidExchangeCreate({
    mutation: {
      onSuccess: () => {
        const queryKey = getCustomerPlaidLinkRetrieveQueryKey(companyUuid)
        queryClient.invalidateQueries({ queryKey: queryKey })

        toast.success("Bank accounts linked successfully!")

        goNext()
      },
    },
  })

  const { open: openPlaidModal, ready: plaidReady } = usePlaidLink({
    token: token,
    onSuccess: (publicToken) => {
      exchangeToken({ companyUuid: companyUuid, data: { publicToken } })
    },
  })

  return (
    <>
      <Button
        type="button"
        variant="default"
        disabled={!plaidReady}
        onClick={() => openPlaidModal()}
        className="w-full"
      >
        <LinkIcon />
        {plaidReady ? "Connect Your Account" : "Loading..."}
      </Button>
    </>
  )
}
